/**
 * @file Contributor UI — BackgroundDataReviewersBlock.
 * Name + email in one row, directly below Data Review.
 */

import React from "react";
import type { FormField } from "../../../../../../interfaces/formInterfaces";
import BackgroundFieldLabel from "./BackgroundFieldLabel";
import {
  dataReviewerEmailKey,
  dataReviewerNameKey,
} from "../utils/backgroundDataReviewers";

type BackgroundDataReviewersBlockProps = {
  active: boolean;
  reviewerGroups: number[];
  values: Record<string, string | File>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  errors?: Record<string, string>;
  readonly?: boolean;
  nameField?: FormField | null;
  emailField?: FormField | null;
};

const BackgroundDataReviewersBlock: React.FC<BackgroundDataReviewersBlockProps> = ({
  active,
  reviewerGroups,
  values,
  onChange,
  errors = {},
  readonly = false,
  nameField,
  emailField,
}) => {
  if (!active || reviewerGroups.length === 0) {
    return null;
  }

  const nameLabel = nameField?.label ?? "Data reviewers name";
  const emailLabel = emailField?.label ?? "Data reviewers email";
  const showReviewerNo = reviewerGroups.length > 1;

  return (
    <div className="data-reviewers-inline">
      {reviewerGroups.map((groupIndex, idx) => {
        const reviewerNo = String(idx + 1).padStart(2, "0");
        const nameKey = dataReviewerNameKey(groupIndex);
        const emailKey = dataReviewerEmailKey(groupIndex);

        return (
          <div key={groupIndex} className="two-field-row data-reviewer-pair-row">
            {showReviewerNo ? (
              <p className="data-reviewer-pair-heading">Reviewer {reviewerNo}</p>
            ) : null}

            <div className="two-field-row-inner">
              <div
                className={`two-field-cell ${errors[nameKey] ? "field-has-error" : ""}`}
              >
                <BackgroundFieldLabel
                  label={
                    showReviewerNo ? `${nameLabel} (${reviewerNo})` : nameLabel
                  }
                  isMandatory={!readonly}
                  infoText={showReviewerNo ? undefined : nameField?.infoText}
                />
                <input
                  type="text"
                  name={nameKey}
                  value={(values[nameKey] as string) || ""}
                  onChange={onChange}
                  disabled={readonly}
                  readOnly={readonly}
                  className={readonly ? "readonly-input" : undefined}
                  autoComplete="name"
                />
                {errors[nameKey] ? (
                  <span className="error-message">{errors[nameKey]}</span>
                ) : null}
              </div>

              <div
                className={`two-field-cell ${errors[emailKey] ? "field-has-error" : ""}`}
              >
                <BackgroundFieldLabel
                  label={
                    showReviewerNo ? `${emailLabel} (${reviewerNo})` : emailLabel
                  }
                  isMandatory={!readonly}
                  infoText={showReviewerNo ? undefined : emailField?.infoText}
                />
                <input
                  type="text"
                  inputMode="email"
                  name={emailKey}
                  value={(values[emailKey] as string) || ""}
                  onChange={onChange}
                  disabled={readonly}
                  readOnly={readonly}
                  className={readonly ? "readonly-input" : undefined}
                  autoComplete="email"
                />
                {errors[emailKey] ? (
                  <span className="error-message">{errors[emailKey]}</span>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** React component — Background Data Reviewers Block. */
export default BackgroundDataReviewersBlock;
